import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { getAnchorTrendWeekly } from '@/lib/admin/insights-api';

// Deterministic HSL color per anchor id
function colorFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return `hsl(${h} 70% 50%)`;
}

export function AnchorTrendChart({ surveyId }: { surveyId: string }) {
  const q = useQuery({
    queryKey: ['admin', 'anchor-trend', surveyId],
    queryFn: () => getAnchorTrendWeekly(surveyId, 12),
  });

  const { series, weeks, anchors } = useMemo(() => {
    const rows = q.data ?? [];
    const weekSet = new Map<string, Record<string, number | string | null>>();
    const anchorMap = new Map<string, string>();
    for (const r of rows) {
      anchorMap.set(r.anchor_id, r.name);
      const w = r.week_start;
      const row = weekSet.get(w) ?? { week_start: w };
      row[r.anchor_id] = r.suppressed ? null : r.mean;
      weekSet.set(w, row);
    }
    const weeksArr = Array.from(weekSet.values()).sort((a, b) =>
      String(a.week_start).localeCompare(String(b.week_start)),
    );
    return {
      series: weeksArr,
      weeks: weeksArr.length,
      anchors: Array.from(anchorMap.entries()).map(([id, name]) => ({ id, name })),
    };
  }, [q.data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-heading">Anchor trends (12 weeks)</CardTitle>
        <CardDescription>Weekly mean per anchor. Weeks with k&lt;5 responses are hidden.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : weeks === 0 || anchors.length === 0 ? (
          <p className="text-sm text-muted-foreground">Not enough data yet.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="week_start"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => String(v).slice(5)}
                />
                <YAxis domain={[1, 5]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {anchors.map((a) => (
                  <Line
                    key={a.id}
                    type="monotone"
                    dataKey={a.id}
                    name={a.name}
                    stroke={colorFor(a.id)}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
